import { ErrorHandler, Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Message } from './message';
import { MessageService } from './message.service';
import { Observable, of, Subject, Subscription, TimeoutError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';
import { customError } from './control-utils';
import { environment } from 'src/environments/environment';

export interface ErroPadraoResponse {
    timestamp: number;
    datahora: string;
    status: number;
    message?: string;
    messages?: string[];
    path: string;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler, OnDestroy {
    private subscription: Subscription;
    private debugEnabled = environment.production ? false : true;
    constructor(private messageService: MessageService) {
        this.subscription = GlobalError$.subscribe({
            next: err => this.handleError(err)
        });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    handleError(error: any): void {
        if (this.debugEnabled) {
            console.debug(`>>GlobalErrorHandler.handleError: ${error}`);
        }
        //console.dir('handleError()', error);
        if (error.promise) {
            error = error.reason || error.rejection || error;
        }

        if (error && error.error) {
            //erro levantado pelo backend
            this.formataExibeErro(error.error as ErroPadraoResponse);
        } else if (error instanceof HttpErrorResponse) {
            if (error.status <= 0) {
                this.messageService.error(Message.ERRO_REDE);
            } else if (error.status == 401) {
                this.messageService.error(Message.ERRO_ACESSO_PROIBIDO);
                throw error;
            } else if (error.status == 403) {
                this.messageService.error(Message.ERRO_ACESSO_NEGADO);
                throw error;
            } else if (error.status == 404) {
                throw error;
            } else {
                setTimeout(() => {
                    //Timeout de meio segundo para evitar exibição de erro ao fazer log off da aplicação
                    this.messageService.error(Message.ERRO_GERAL);
                }, 500);
            }
        } else {
            if (error instanceof TimeoutError) {
                this.messageService.error(Message.ERRO_TIMEOUT);
            } else {
                setTimeout(() => {
                    //Timeout de meio segundo para evitar exibição de erro ao fazer log off da aplicação
                    this.messageService.error(Message.ERRO_GERAL);
                }, 500);
            }
        }
    }

    formataExibeErro(error: ErroPadraoResponse) {
        if (error.message && error.status === 422) {
            this.messageService.error(error.message);
        } else if (error.messages && error.messages.length > 0) {
            error.messages.forEach(msg => {
                this.messageService.error(msg);
            });
        } else {
            if (this.debugEnabled) {
                console.debug(`>>GlobalErrorHandler.formataExibeErro: ${JSON.stringify(error)}`);
            }
            this.messageService.error(`Erro ao executar operação. Razão: ${JSON.stringify(error)}`);
        }
    }
}

const GlobalError$ = new Subject<any>();

export function addGlobalError(err: any): void {
    GlobalError$.next(err);
}

/**
 * Em caso de erro adiciona o evento no {@link GlobalError$} e retorna um {@link Observable} contendo o valor informado.
 * Deve ser utilizado sempre em um pipe de um Observable de consulta a um serviço.
 * @param value Valor contido no Observable retornado.
 */
export function onError<T, V extends T>(value: V): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>): Observable<T> =>
        source.pipe(
            catchError(err => {
                addGlobalError(err);
                return of(value);
            })
        );
}

export function catchValidationErrors<T>(form: FormGroup): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>): Observable<T> =>
        source.pipe(
            catchError(err => {
                if (err.status == 400 && err.error?.errosValidacao) {
                    const errosValidacao = err.error.errosValidacao;
                    const globalMessages: string[] = err.error.mensagens || [];

                    Object.keys(errosValidacao).forEach(prop => {
                        const formControl = form.get(prop);
                        const message: string = errosValidacao[prop];
                        if (formControl) {
                            formControl.setErrors(customError(message));
                        } else if (message) {
                            globalMessages.push(message);
                        }
                    });

                    if (globalMessages.length) {
                        err.error.mensagens = globalMessages;
                    }
                }

                throw err;
            })
        );
}
